import { useEffect, useRef } from "react";
import Radio from "@/components/Radio";
import HtmlRichTextEditor from "@/components/RichTextEditor/HtmlRichTextEditor";
import TextField from "@/components/TextField";
import { currencyFormatter } from "@/lib/currencyHelpers";
import { Controller } from "react-hook-form";
import { NumberFormatBase } from "react-number-format";
import Select from "@/components/Select";
import SubSectionHeading from "@/components/SubSectionHeading";
import { ServiceLevel, isExecuSearchServiceLevel } from "@/pages/_layouts/jobs/types";

const COMPENSATION_TYPES = [
  { label: "Show Compensation", value: false },
  { label: "Hide Compensation", value: true }
];

const COMPENSATION_SCALES = [
  { label: "Salary", value: "Salary" },
  { label: "Weekly", value: "Weekly" },
  { label: "Hourly", value: "Hourly" }
];

const YEARS_OF_EXPERIENCE_OPTIONS = [
  { value: "0", label: "Not Applicable" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5+" }
];

const EDUCATION_OPTIONS = [
  { value: "N/A", label: "Not Applicable" },
  { value: "High School Graduate", label: "High School Graduate" },
  { value: "GED", label: "GED" },
  { value: "Vocational Qualification", label: "Vocational Qualification" },
  { value: "Bachelors", label: "Bachelors" },
  { value: "Masters", label: "Masters" },
  { value: "Doctorate or Higher", label: "Doctorate or Higher" }
];

export function CompensationStep({ form, isEdit = false }: { form: any; isEdit?: boolean }) {
  const {
    control,
    watch,
    formState: { errors },
    trigger,
    unregister,
    setValue
  } = form;

  const paySchedule = watch("paySchedule");
  const serviceLevel = watch("serviceLevel");
  const applyVia = watch("applyVia");
  const isAdvancedOrExecutive = isExecuSearchServiceLevel(serviceLevel);

  const defaultHideCompensation = isExecuSearchServiceLevel(serviceLevel) && applyVia === "Facility Website";

  // Track the previous service level to detect changes
  const prevLevelRef = useRef(serviceLevel);
  // Track if we've completed initial setup
  const hasAppliedInitialDefaultRef = useRef(false);

  useEffect(() => {
    const prevLevel = prevLevelRef.current;
    const serviceLevelChanged = prevLevel !== serviceLevel;

    // On initial mount:
    // - In create mode: Apply the service-level default (user may have changed service level on Step 1)
    // - In edit mode: Don't apply default (preserve the saved value)
    if (!hasAppliedInitialDefaultRef.current) {
      hasAppliedInitialDefaultRef.current = true;
      prevLevelRef.current = serviceLevel;

      if (!isEdit) {
        setValue("hideCompensationFromJobBoard", defaultHideCompensation);
      }
      return;
    }

    // Only proceed if service level actually changed
    if (!serviceLevelChanged) {
      return;
    }

    // Persist / restore dev-only hidden fields when toggling Assisted ↔ Advanced/Executive
    if (prevLevel === ServiceLevel.ASSISTED && isExecuSearchServiceLevel(serviceLevel)) {
      const hiddenFields = {
        benefits: watch("benefits"),
        additionalIncome: watch("additionalIncome")
      };
      localStorage.setItem("jobPostForm_hiddenFields", JSON.stringify(hiddenFields));
    }

    if (isExecuSearchServiceLevel(prevLevel) && serviceLevel === ServiceLevel.ASSISTED) {
      const savedFields = localStorage.getItem("jobPostForm_hiddenFields");
      if (savedFields) {
        const { benefits, additionalIncome } = JSON.parse(savedFields);
        setValue("benefits", benefits);
        setValue("additionalIncome", additionalIncome);
      }
    }

    // Apply the new service-level default for compensation visibility
    setValue("hideCompensationFromJobBoard", defaultHideCompensation);

    // Unregister min/max when service level flips (ensures RHF refresh)
    unregister("compensation.baseMinimum");
    unregister("compensation.baseMaximum");
    unregister("compensation.totalAnticipated");

    prevLevelRef.current = serviceLevel;
  }, [serviceLevel, applyVia, defaultHideCompensation, unregister, watch, setValue, isEdit]);

  const getCompensationLabel = () => {
    switch (paySchedule) {
      case "Weekly":
        return "Weekly";
      case "Hourly":
        return "Hourly";
      default:
        return "Salary";
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-headline-small">Enter compensation and benefits for the job.</div>

      <div className="col-span-full">
        <p className="text-title-medium mb-2">Compensation Data Privacy</p>
        <p className="text-body-medium mb-2">
          Compensation data is required; however, you may choose to hide compensation data from job seekers and
          applicants. Compensation data improves services and helps to provide accurate job recommendations.
        </p>
        <Controller
          control={control}
          name="hideCompensationFromJobBoard"
          render={({ field }) => (
            <div className="flex flex-col" data-testid="compensation-type-radio-group">
              {COMPENSATION_TYPES.map((option, i) => (
                <div className={"py-3"} key={i}>
                  <Radio
                    name={`compensationType-${option.value}`}
                    label={option.label}
                    checked={field.value === option.value}
                    onChange={() => {
                      field.onChange(option.value);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        />
      </div>
      <div className="col-span-full">
        <p className="text-title-medium mb-2">Compensation Scale</p>
        <Controller
          control={control}
          name="paySchedule"
          render={({ field }) => (
            <div className="flex flex-col" data-testid="compensation-scale-radio-group">
              {COMPENSATION_SCALES.map((option, i) => (
                <div className={"py-3"} key={i}>
                  <Radio
                    name={`compensationScale-${option.value}`}
                    label={option.label}
                    checked={field.value === option.value}
                    onChange={() => {
                      field.onChange(option.value);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="col-span-4">
          <Controller
            control={control}
            name="compensation.baseMinimum"
            rules={{
              validate: (value) => {
                // Enforce > 0 only when the field is required
                if (value !== undefined && value !== 0 && value <= 0) {
                  return `Base Compensation (${getCompensationLabel()} Min) must be greater than 0`;
                }
                return true;
              }
            }}
            render={({ field }) => (
              <NumberFormatBase
                disabled={false}
                value={field.value ?? ""}
                format={currencyFormatter}
                name="compensation.baseMinimum"
                className="w-full"
                customInput={TextField}
                error={!!errors.compensation?.baseMinimum}
                errorText={errors.compensation?.baseMinimum?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.floatValue ?? undefined);
                  trigger("compensation.baseMinimum");
                  trigger("compensation.baseMaximum");
                }}
                required={false}
                leadingIcon="attach_money"
                focusedLabel={`Base Compensation (${getCompensationLabel()} Min)`}
                label="Base (Min)"
              />
            )}
          />
        </div>
        <div className="col-span-4">
          <Controller
            control={control}
            name="compensation.baseMaximum"
            rules={{
              validate: (value) => {
                const minValue = watch("compensation.baseMinimum");

                if (value === undefined || value === null) return true;
                // Both values are positive and max is less than min
                if (value > 0 && minValue > 0 && value < minValue)
                  return `Base Compensation (${getCompensationLabel()} Max) must be ≥ Base Compensation (${getCompensationLabel()} Min)`;
                return true;
              }
            }}
            render={({ field }) => (
              <NumberFormatBase
                disabled={false}
                value={field.value ?? ""}
                format={currencyFormatter}
                name="compensation.baseMaximum"
                className="w-full"
                customInput={TextField}
                error={!!errors.compensation?.baseMaximum}
                errorText={errors.compensation?.baseMaximum?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.floatValue ?? undefined);
                  trigger("compensation.baseMaximum");
                }}
                required={false}
                leadingIcon="attach_money"
                focusedLabel={`Base Compensation (${getCompensationLabel()} Max)`}
                label="Base (Max)"
              />
            )}
          />
        </div>
        <div className="col-span-4">
          <Controller
            control={control}
            name="compensation.totalAnticipated"
            render={({ field }) => (
              <NumberFormatBase
                disabled={false}
                value={field.value || ""}
                format={(value) => (value ? currencyFormatter(value) : "")}
                name="compensation.totalAnticipated"
                className="w-full"
                customInput={TextField}
                error={!!errors.compensation?.totalAnticipated}
                errorText={errors.compensation?.totalAnticipated?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.floatValue || undefined);
                }}
                leadingIcon="attach_money"
                label="Total Comp"
                focusedLabel="Total Compensation"
              />
            )}
          />
        </div>
      </div>

      {isAdvancedOrExecutive && (
        <>
          <div className="col-span-full mt-8">
            <SubSectionHeading className="mb-4">Education and Experience</SubSectionHeading>
            <div className="space-y-6">
              <div className="col-span-full">
                <Controller
                  name="requiredExperienceInYears"
                  control={control}
                  rules={{ required: "Years of experience is required" }}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      label="Years of Experience"
                      options={YEARS_OF_EXPERIENCE_OPTIONS}
                      className="w-full"
                      value={value}
                      required
                      error={!!errors.requiredExperienceInYears}
                      errorText={errors.requiredExperienceInYears?.message as string}
                      onSelect={onChange}
                    />
                  )}
                />
              </div>
              <div className="col-span-full">
                <Controller
                  name="requiredEducation"
                  control={control}
                  rules={{ required: "Education is required" }}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      label="Education"
                      options={EDUCATION_OPTIONS}
                      className="w-full"
                      value={value}
                      required
                      error={!!errors.requiredEducation}
                      errorText={errors.requiredEducation?.message as string}
                      onSelect={onChange}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {!isAdvancedOrExecutive && (
        <div className="space-y-6">
          <div className="col-span-full">
            <Controller
              name="benefits"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HtmlRichTextEditor
                  label="Benefits"
                  placeholder="Description"
                  initialContent={value}
                  onChange={onChange}
                />
              )}
            />
          </div>

          <div className="col-span-full">
            <Controller
              name="additionalIncome"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HtmlRichTextEditor
                  label="Additional Income"
                  placeholder="Description"
                  initialContent={value}
                  onChange={onChange}
                  error={errors.additionalIncome?.message?.toString()}
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
